import { z } from 'zod';

/**
 * Work-entry schema for the field-worker's mobile timesheet form.
 *
 * Mirrors WorkEntryFormModal validation rules:
 *   - activity + task must be selected
 *   - start < end (both required as HH:mm strings)
 *   - break + standby cannot exceed total span
 *   - notes optional (empty string default — keep z.input === z.output)
 *
 * Cross-field validation is applied at the object level so RHF Resolver
 * type inference stays clean.
 */
export const workEntrySchema = z
  .object({
    activityId: z.string().min(1, 'Select an activity'),
    workOrderId: z.string().min(1),
    workOrderNumber: z.string().min(1),
    scaffoldTaskId: z.string().min(1, 'Select a task'),
    scaffoldNumber: z.string().min(1),
    taskName: z.string().min(1),
    // Required (empty allowed) so z.input === z.output — see playbook §3.5.
    clientName: z.string(),
    siteName: z.string(),
    startTime: z.string().regex(/^\d{2}:\d{2}$/, 'Required'),
    endTime: z.string().regex(/^\d{2}:\d{2}$/, 'Required'),
    breakMinutes: z.number().int().min(0),
    standbyMinutes: z.number().int().min(0),
    notes: z.string().max(500, 'Notes must be 500 characters or fewer'),
  })
  .refine(
    (data) => {
      const [sh, sm] = data.startTime.split(':').map(Number);
      const [eh, em] = data.endTime.split(':').map(Number);
      return eh * 60 + em > sh * 60 + sm;
    },
    { message: 'End time must be after start time', path: ['endTime'] },
  )
  .refine(
    (data) => {
      const [sh, sm] = data.startTime.split(':').map(Number);
      const [eh, em] = data.endTime.split(':').map(Number);
      const span = eh * 60 + em - (sh * 60 + sm);
      return data.breakMinutes + data.standbyMinutes < span;
    },
    {
      message: 'Break + Standby exceeds duration',
      path: ['standbyMinutes'],
    },
  );

export type WorkEntryFormValues = z.infer<typeof workEntrySchema>;

export const defaultWorkEntryValues: WorkEntryFormValues = {
  activityId: '',
  workOrderId: '',
  workOrderNumber: '',
  scaffoldTaskId: '',
  scaffoldNumber: '',
  taskName: '',
  clientName: '',
  siteName: '',
  startTime: '08:00',
  endTime: '12:00',
  breakMinutes: 0,
  standbyMinutes: 0,
  notes: '',
};
